import { BoundingBox } from './coordinateUtils';

interface SpatialItem {
  id: string;
  bounds: BoundingBox;
}

class QuadTreeNode {
  bounds: BoundingBox;
  items: SpatialItem[] = [];
  children: QuadTreeNode[] | null = null;
  maxItems: number;
  maxDepth: number;
  depth: number;

  constructor(bounds: BoundingBox, maxItems: number = 8, maxDepth: number = 6, depth: number = 0) {
    this.bounds = bounds;
    this.maxItems = maxItems;
    this.maxDepth = maxDepth;
    this.depth = depth;
  }

  private subdivide() {
    const { x, y, width, height } = this.bounds;
    const hw = width / 2;
    const hh = height / 2;

    this.children = [
      new QuadTreeNode({ x, y, width: hw, height: hh }, this.maxItems, this.maxDepth, this.depth + 1),
      new QuadTreeNode({ x: x + hw, y, width: hw, height: hh }, this.maxItems, this.maxDepth, this.depth + 1),
      new QuadTreeNode({ x, y: y + hh, width: hw, height: hh }, this.maxItems, this.maxDepth, this.depth + 1),
      new QuadTreeNode({ x: x + hw, y: y + hh, width: hw, height: hh }, this.maxItems, this.maxDepth, this.depth + 1),
    ];

    for (const item of this.items) {
      for (const child of this.children!) {
        if (this.boundsIntersect(item.bounds, child.bounds)) {
          child.insert(item);
        }
      }
    }
    this.items = [];
  }

  private boundsIntersect(a: BoundingBox, b: BoundingBox): boolean {
    return (
      a.x < b.x + b.width &&
      a.x + a.width > b.x &&
      a.y < b.y + b.height &&
      a.y + a.height > b.y
    );
  }

  insert(item: SpatialItem): boolean {
    if (!this.boundsIntersect(item.bounds, this.bounds)) {
      return false;
    }

    if (this.children) {
      for (const child of this.children) {
        child.insert(item);
      }
      return true;
    }

    this.items.push(item);

    if (this.items.length > this.maxItems && this.depth < this.maxDepth) {
      this.subdivide();
    }

    return true;
  }

  query(queryBounds: BoundingBox): SpatialItem[] {
    if (!this.boundsIntersect(queryBounds, this.bounds)) {
      return [];
    }

    const results: SpatialItem[] = [];

    for (const item of this.items) {
      if (this.boundsIntersect(item.bounds, queryBounds)) {
        results.push(item);
      }
    }

    if (this.children) {
      for (const child of this.children) {
        results.push(...child.query(queryBounds));
      }
    }

    return results;
  }

  queryPoint(x: number, y: number): SpatialItem[] {
    return this.query({ x, y, width: 0, height: 0 });
  }
}

export class SpatialIndex {
  private root: QuadTreeNode | null = null;

  clear() {
    this.root = null;
  }

  build(items: SpatialItem[]) {
    if (items.length === 0) {
      this.root = null;
      return;
    }

    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;

    for (const item of items) {
      minX = Math.min(minX, item.bounds.x);
      minY = Math.min(minY, item.bounds.y);
      maxX = Math.max(maxX, item.bounds.x + item.bounds.width);
      maxY = Math.max(maxY, item.bounds.y + item.bounds.height);
    }

    this.root = new QuadTreeNode({
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    });

    for (const item of items) {
      this.root.insert(item);
    }
  }

  query(bounds: BoundingBox): SpatialItem[] {
    if (!this.root) return [];
    return this.root.query(bounds);
  }

  queryPoint(x: number, y: number): SpatialItem[] {
    if (!this.root) return [];
    return this.root.queryPoint(x, y);
  }
}
